import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const TimelineSection = () => {
  const [expandedItem, setExpandedItem] = useState(null);

  const timelineData = [
    {
      id: 1,
      year: "2021-2025",
      title: "BSc (Honours) in Software Engineering",
      subtitle: "NSBM Green University - Homagama",
      icon: "GraduationCap",
      color: "primary",
      summary: "MOHE (SECOND CLASS UPPER DIVISION) - Award GPA: 3.44. Faculty of Computing, NSBM Green University.",
      details: `Completed a comprehensive software engineering degree with strong focus on full-stack development, data structures, algorithms, and modern web technologies. Actively participated in IEEE student branch activities and community development initiatives.\n\nKey achievements include multiple mobile app projects, web applications, and AI/ML implementations. Organized tech workshops and participated in coding competitions including Arduino competitions and mobile application development challenges with IEEE student branch of NSBM.`,
      skills: ["Software Engineering", "Full-Stack Development", "Mobile Development", "AI/ML", "Community Leadership"],
      impact: "Graduated with Second Class Upper Division, GPA: 3.44"
    },
    {
      id: 2,
      year: "2023-2024",
      title: "Intern Software Engineer",
      subtitle: "Sri Lanka Institute of Biotechnology (SLIBTEC)",
      icon: "Code",
      color: "success",
      summary: "Designed and implemented comprehensive inventory management system for SLIBTEC.",
      details: `Developed using PHP, JavaScript, CSS, REST API, Bootstrap, MySQL, and server configuration. Managed inventories for key categories: Admin, Finance, HR, Procurement, Engineering, Business, Research, and User.\n\nStreamlined inventory processes to improve efficiency and accuracy. Integrated a user-friendly interface for seamless access and management of inventory data across 8 departments.`,
      skills: ["PHP", "JavaScript", "CSS", "REST API", "Bootstrap", "MySQL", "Server Configuration"],
      impact: "Streamlined inventory management across 8 departments with improved efficiency"
    },
    {
      id: 3,
      year: "2024-2025",
      title: "Associate Software Engineer",
      subtitle: "Sri Lanka Institute of Biotechnology (SLIBTEC)",
      icon: "Rocket",
      color: "brand-purple",
      summary: "Enhanced and upgraded SLIBTEC Inventory System-2025 with improved architecture and advanced features.",
      details: `System Upgrade: Enhanced system architecture using PHP, JavaScript, REST API, and MySQL for improved scalability, performance, and security. Added advanced reporting, automated alerts, and audit trails to increase transparency and accountability.\n\nProcess Automation: Streamlined procurement and stock replenishment processes, reducing manual errors and boosting efficiency. Improved UX/UI by redesigning the interface for a more intuitive and user-friendly experience in managing inventory data and tracking equipment.`,
      skills: ["PHP", "JavaScript", "REST API", "MySQL", "System Architecture", "Process Automation"],
      impact: "Reduced manual errors and boosted efficiency significantly with automated processes"
    },
    {
      id: 4,
      year: "2025-Present",
      title: "Software Engineer",
      subtitle: "Techdoz - Recruitment Web Application",
      icon: "Users",
      color: "accent",
      summary: "Developing comprehensive recruitment platform with AI-powered CV analysis and skill assessment.",
      details: `Tech Stack: Developed using Flask (backend), React.js (frontend), NLTK (NLP tasks), MySQL (database), and AWS (deployment). Core Features: Secure user authentication, CV upload/analysis with NLP, performance scoring, and quiz system for skill assessment.\n\nCreated responsive admin interface based on CV analysis and quiz results. Selection based on CV analysis and quiz results, with a responsive UI built using React.js. Successfully deployed on AWS with thorough testing ensuring robust performance and error handling.`,
      skills: ["Flask", "React.js", "NLTK", "MySQL", "AWS", "NLP", "Full-Stack Development"],
      impact: "Automated CV screening reduces manual review time by 70%"
    },
    {
      id: 5,
      year: "2022-Present",
      title: "Technical Content Creator",
      subtitle: "YouTube - Full-Stack Development Education",
      icon: "Crown",
      color: "warning",
      summary: "Creating educational content and project tutorials for the developer community.",
      details: `Creating comprehensive tutorials on full-stack development, sharing knowledge about software engineering concepts and real-world project implementations. Built a community of developers through educational content on technologies including React.js, Spring Boot, PHP, Flutter, and more.\n\nProjects covered include Full-Stack Online Job Portal System (React.js, Spring Boot, MySQL, Kafka), Point-Of-Sale (POS) Systems for Inventory Management (PHP, MySQL, AJAX, Chart.js), Full-Stack Food Ordering App (Flutter, Firebase), and many other real-world applications. Contributing to the tech education ecosystem and helping developers learn modern development practices.`,
      skills: ["Content Creation", "Technical Documentation", "Full-Stack Development", "Community Building", "Video Production"],
      impact: "Building developer community through practical tutorials and project implementations"
    }
  ];

  const toggleExpanded = (id) => {
    setExpandedItem(expandedItem === id ? null : id);
  };

  const getColorClasses = (color) => {
    const colorMap = {
      primary: "bg-primary text-white",
      success: "bg-success text-white",
      "brand-purple": "bg-brand-purple text-white",
      accent: "bg-accent text-white",
      warning: "bg-warning text-white"
    };
    return colorMap[color] || "bg-primary text-white";
  };

  return (
    <section className="py-20 bg-white">
      <div className="max-w-6xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-16">
          <div className="inline-flex items-center space-x-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium mb-4">
            <Icon name="Clock" size={16} />
            <span>Professional Journey</span>
          </div>
          <h2 className="text-3xl lg:text-4xl font-bold text-text-primary mb-4">
            My Development Timeline
          </h2>
          <p className="text-xl text-text-secondary max-w-3xl mx-auto">
            From university student to senior engineer - each milestone shaped my expertise 
            and passion for creating exceptional digital experiences.
          </p>
        </div>

        <div className="relative">
          {/* Timeline Line */}
          <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-border hidden lg:block"></div>

          <div className="space-y-12">
            {timelineData.map((item, index) => (
              <div key={item.id} className="relative">
                {/* Timeline Node */}
                <div className="hidden lg:flex absolute left-6 w-4 h-4 bg-white border-4 border-primary rounded-full"></div>

                <div className="lg:ml-20">
                  <div 
                    className="bg-white rounded-2xl border border-border shadow-premium hover:shadow-xl transition-premium cursor-pointer"
                    onClick={() => toggleExpanded(item.id)}
                  >
                    <div className="p-8">
                      <div className="flex items-start justify-between mb-6">
                        <div className="flex items-center space-x-4">
                          <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${getColorClasses(item.color)}`}>
                            <Icon name={item.icon} size={24} />
                          </div>
                          <div>
                            <div className="text-sm font-medium text-text-muted">{item.year}</div>
                            <h3 className="text-xl font-bold text-text-primary">{item.title}</h3>
                            <p className="text-text-secondary">{item.subtitle}</p>
                          </div>
                        </div>
                        <Icon 
                          name={expandedItem === item.id ? "ChevronUp" : "ChevronDown"} 
                          size={20} 
                          className="text-text-muted"
                        />
                      </div>

                      <p className="text-text-secondary mb-6">{item.summary}</p>

                      {/* Skills Tags */}
                      <div className="flex flex-wrap gap-2 mb-6">
                        {item.skills.map((skill, skillIndex) => (
                          <span 
                            key={skillIndex}
                            className="px-3 py-1 bg-surface text-text-secondary text-sm rounded-full"
                          >
                            {skill}
                          </span>
                        ))}
                      </div>

                      {/* Expanded Content */}
                      {expandedItem === item.id && (
                        <div className="border-t border-border pt-6 space-y-6">
                          <div>
                            <h4 className="font-semibold text-text-primary mb-3">Detailed Story</h4>
                            <div className="text-text-secondary leading-relaxed whitespace-pre-line">
                              {item.details}
                            </div>
                          </div>
                          
                          <div className="bg-success/10 rounded-xl p-4 border border-success/20">
                            <div className="flex items-start space-x-3">
                              <Icon name="TrendingUp" size={20} className="text-success mt-0.5" />
                              <div>
                                <h5 className="font-medium text-success mb-1">Key Impact</h5>
                                <p className="text-text-secondary">{item.impact}</p>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default TimelineSection;